import { useState, useEffect, useRef } from 'react'
import { Send, Bot, User, ChevronDown, ChevronUp, CheckCircle2, XCircle, Loader2, KeyRound, RefreshCw } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { credentialSchema } from '../lib/schemas'
import { useCustomers } from '../hooks/useCustomers'
import { useOnboardingStatus, useStoreCredentials, useSendChat } from '../hooks/useOnboarding'

// ─── Credential intake form ─────────────────────────────────────────────────

function CredentialForm({ customers, onSubmit, submitting }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(credentialSchema),
    defaultValues: { customer_id: '', tenant_id: '', client_id: '', client_secret: '' },
  })

  const err = (field) => errors[field] && (
    <small style={{ color: 'var(--danger)', display: 'block', marginTop: '2px' }}>
      {errors[field].message}
    </small>
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="onboarding-cred-form">
      <h3><KeyRound size={16} /> Service Principal Credentials</h3>
      <p className="onboarding-cred-hint">
        Enter the customer's Entra Service Principal credentials. They will be stored securely in Key Vault.
      </p>

      <div className="form-group">
        <label>Customer</label>
        <select {...register('customer_id')}>
          <option value="">Select a customer…</option>
          {customers.map(c => (
            <option key={c.tenant_id} value={c.tenant_id}>{c.customer_name}</option>
          ))}
        </select>
        {err('customer_id')}
      </div>

      <div className="form-group">
        <label>Customer Entra Tenant ID</label>
        <input type="text" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" {...register('tenant_id')} />
        {err('tenant_id')}
      </div>

      <div className="form-group">
        <label>Service Principal Client ID</label>
        <input type="text" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" {...register('client_id')} />
        {err('client_id')}
      </div>

      <div className="form-group">
        <label>Service Principal Client Secret</label>
        <input type="password" placeholder="Secret value (not the secret ID)" {...register('client_secret')} />
        {err('client_secret')}
      </div>

      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? <Loader2 size={16} className="spin" /> : <KeyRound size={16} />}
        {submitting ? 'Storing…' : 'Store credentials & start chat'}
      </button>
    </form>
  )
}

// ─── Step log panel ──────────────────────────────────────────────────────────

function StepLog({ steps }) {
  const [expanded, setExpanded] = useState(false)
  if (!steps?.length) return null
  const shown = expanded ? steps : steps.slice(0, 4)
  return (
    <div className="onboarding-steplog">
      <div className="onboarding-steplog-header" onClick={() => setExpanded(v => !v)}>
        <strong>Onboarding steps</strong>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      {shown.map((s, i) => (
        <div key={i} className={`steplog-row steplog-${s.status}`}>
          {s.status === 'complete' && <CheckCircle2 size={13} />}
          {s.status === 'failed'   && <XCircle size={13} />}
          {s.status === 'started'  && <Loader2 size={13} className="spin" />}
          <span className="steplog-name">{s.stepName}</span>
          <span className="steplog-status">{s.status}</span>
          {s.errorMessage && <span className="steplog-error">{s.errorMessage}</span>}
        </div>
      ))}
      {!expanded && steps.length > 4 && (
        <button className="steplog-more" onClick={() => setExpanded(true)}>
          +{steps.length - 4} more steps
        </button>
      )}
    </div>
  )
}

// ─── Chat bubble ─────────────────────────────────────────────────────────────

function Bubble({ role, content }) {
  return (
    <div className={`chat-bubble chat-bubble-${role}`}>
      <div className="chat-bubble-icon">
        {role === 'assistant' ? <Bot size={15} /> : <User size={15} />}
      </div>
      <div className="chat-bubble-text">{content}</div>
    </div>
  )
}

// ─── Main Onboarding page ────────────────────────────────────────────────────

export default function Onboarding() {
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [error, setError] = useState(null)
  const [phase, setPhase] = useState('select')  // 'select' | 'loading' | 'creds' | 'chat'

  const bottomRef = useRef(null)

  const { data: customers = [] } = useCustomers()
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useOnboardingStatus(selectedCustomer?.tenant_id ?? null)
  const storeCredentials = useStoreCredentials()
  const sendChat = useSendChat(selectedCustomer?.tenant_id ?? '')

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Transition out of 'loading' once the status query resolves
  useEffect(() => {
    if (phase !== 'loading' || !selectedCustomer || statusLoading) return

    if (status?.spSecretRef) {
      const workspaceCount = status.workspaces?.length ?? 0
      let intro
      if (status.onboardingStatus === 'complete') {
        intro = `Onboarding for **${selectedCustomer.customer_name}** is complete — ${workspaceCount} workspace(s) created. How can I help?`
      } else if (workspaceCount > 0) {
        const names = status.workspaces.map(w => w.workspaceName).join(', ')
        intro = `Welcome back! **${selectedCustomer.customer_name}** onboarding is in progress. Workspaces already created: ${names}. Want to continue with the remaining ones, or do something else?`
      } else {
        intro = `Credentials are stored for **${selectedCustomer.customer_name}**. Ready to start — shall I go ahead with the standard 7-workspace setup?`
      }
      setMessages([{ role: 'assistant', content: intro }])
      setPhase('chat')
    } else {
      setPhase('creds')
    }
  }, [phase, selectedCustomer, status, statusLoading])

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer)
    setMessages([])
    setError(null)
    setPhase('loading')
  }

  const handleCredSubmit = async (form) => {
    setError(null)
    try {
      await storeCredentials.mutateAsync(form)
      setPhase('chat')
      setMessages([{
        role: 'assistant',
        content: `Credentials stored successfully for **${selectedCustomer.customer_name}**. I'm connected to their Fabric tenant. How would you like to proceed? I can create a workspace, list existing workspaces, or walk you through the full onboarding.`,
      }])
    } catch (e) {
      setError('Failed to store credentials: ' + e.message)
    }
  }

  const handleSend = async () => {
    const text = input.trim()
    if (!text || sendChat.isPending) return

    const newMessages = [...messages, { role: 'user', content: text }]
    setMessages(newMessages)
    setInput('')
    setError(null)

    try {
      const result = await sendChat.mutateAsync(newMessages)
      setMessages(result.messages || [...newMessages, { role: 'assistant', content: result.content }])
      refetchStatus()
    } catch (e) {
      setError('Chat failed: ' + e.message)
      setMessages(newMessages)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const reset = () => {
    setPhase('select')
    setSelectedCustomer(null)
    setMessages([])
    setError(null)
  }

  return (
    <div className="onboarding-page">
      <div className="page-header">
        <h1>Customer Onboarding</h1>
        {selectedCustomer && (
          <button className="btn-secondary" onClick={reset}>
            <RefreshCw size={15} /> Switch customer
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.6rem 1rem', marginBottom: '1.25rem', background: 'rgba(237, 137, 54, 0.1)', border: '1px solid var(--warning)', borderRadius: '8px', color: 'var(--warning)', fontSize: '0.85rem', fontWeight: 500 }}>
        ⚠ Work in progress — onboarding automation is under active development.
      </div>

      {error && (
        <div className="notification notification-error">
          <XCircle size={15} />
          <span>{error}</span>
        </div>
      )}

      {/* Phase: loading status */}
      {phase === 'loading' && (
        <div style={{ color: 'var(--text-muted)', padding: '1rem 0' }}>
          <Loader2 size={16} className="spin" style={{ display: 'inline', marginRight: '6px' }} />
          Checking onboarding status for {selectedCustomer?.customer_name}…
        </div>
      )}

      {/* Phase: select customer */}
      {phase === 'select' && (
        <div className="onboarding-select">
          <p>Select a customer to begin or continue their onboarding.</p>
          <div className="onboarding-customer-grid">
            {customers.map(c => (
              <button
                key={c.tenant_id}
                className="onboarding-customer-card"
                onClick={() => handleSelectCustomer(c)}
              >
                <strong>{c.customer_name}</strong>
                <span className="tenant-id-small">{c.tenant_id}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phase: credential intake */}
      {phase === 'creds' && selectedCustomer && (
        <div className="onboarding-cred-phase">
          <p>
            Onboarding <strong>{selectedCustomer.customer_name}</strong>. No Service Principal credentials found — enter them below.
          </p>
          <CredentialForm
            customers={[selectedCustomer]}
            onSubmit={handleCredSubmit}
            submitting={storeCredentials.isPending}
          />
        </div>
      )}

      {/* Phase: chat */}
      {phase === 'chat' && selectedCustomer && (
        <div className="onboarding-chat-layout">
          <div className="onboarding-sidebar">
            <div className="onboarding-sidebar-header">
              <strong>{selectedCustomer.customer_name}</strong>
              <span className={`status-badge ${status?.onboardingStatus === 'complete' ? 'active' : 'inactive'}`}>
                {status?.onboardingStatus || 'pending'}
              </span>
            </div>
            {status?.workspaces?.length > 0 && (
              <div className="onboarding-workspaces">
                <strong>Workspaces created</strong>
                {status.workspaces.map(w => (
                  <div key={w.workspaceId} className="onboarding-workspace-row">
                    <CheckCircle2 size={13} className="success" />
                    {w.workspaceName}
                  </div>
                ))}
              </div>
            )}
            <StepLog steps={status?.steps} />
          </div>

          <div className="onboarding-chat">
            <div className="chat-messages">
              {messages.map((m, i) => {
                const text = typeof m.content === 'string'
                  ? m.content
                  : m.content?.map?.(b => b.text || '').join('') || ''
                if (!text) return null
                return <Bubble key={i} role={m.role} content={text} />
              })}
              {sendChat.isPending && (
                <div className="chat-bubble chat-bubble-assistant">
                  <div className="chat-bubble-icon"><Bot size={15} /></div>
                  <div className="chat-bubble-text chat-thinking">
                    <Loader2 size={14} className="spin" /> Thinking…
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="chat-input-row">
              <textarea
                className="chat-input"
                placeholder="Tell me what to do next…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                disabled={sendChat.isPending}
              />
              <button
                className="btn-primary chat-send"
                onClick={handleSend}
                disabled={sendChat.isPending || !input.trim()}
              >
                {sendChat.isPending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
